import React, { useMemo } from 'react';
import {
  Users,
  UserCheck,
  Clock,
  Briefcase,
  TrendingDown,
  Plus,
  Calendar,
  GitBranch,
  BarChart3,
  AlertCircle,
  CheckCircle,
  UserPlus,
  LogOut,
  Award,
} from 'lucide-react';

const neomorphicShadow = '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)';
const neomorphicInsetShadow = 'inset 5px 5px 10px rgba(186,199,216,0.45), inset -5px -5px 10px rgba(255,255,255,0.8)';
const colors = {
  primary: '#2E8B98',
  lightPrimary: '#3CA0AE',
  hoverPrimary: '#256F7A',
  success: '#62BB46',
  warning: '#F26522',
  danger: '#DC3545',
  background: '#F8F9FA',
  text: '#212529',
  textSecondary: '#6C757D',
};

const departmentColors = [
  '#2E8B98',
  '#3CA0AE',
  '#256F7A',
  '#62BB46',
  '#F26522',
  '#DC3545',
  '#6C757D',
  '#8B9DC3',
];

/**
 * Calculate statistics from personnel and leave request data
 */
const usePersonnelStats = (personnel, leaveRequests) => {
  return useMemo(() => {
    const totalEmployees = personnel.length;
    const activeEmployees = personnel.filter(p => p.status === 'active').length;
    const onLeaveCount = leaveRequests.filter(
      lr => lr.status === 'approved' && new Date(lr.start_date) <= new Date() && new Date(lr.end_date) >= new Date()
    ).length;
    const terminatedCount = personnel.filter(p => p.status === 'terminated').length;
    const turnoverRate = totalEmployees > 0 ? ((terminatedCount / totalEmployees) * 100).toFixed(1) : 0;

    // Department distribution
    const deptMap = {};
    personnel.forEach(p => {
      const dept = p.department || 'Unknown';
      deptMap[dept] = (deptMap[dept] || 0) + 1;
    });
    const departmentDistribution = Object.entries(deptMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([dept, count]) => ({ dept, count }));

    // Job category breakdown
    const categoryMap = {};
    personnel.forEach(p => {
      const cat = p.job_category || 'Unknown';
      categoryMap[cat] = (categoryMap[cat] || 0) + 1;
    });
    const jobCategories = Object.entries(categoryMap).map(([category, count]) => ({
      category,
      count,
      percentage: ((count / totalEmployees) * 100).toFixed(1),
    }));

    // Upcoming birthdays (next 30 days)
    const today = new Date();
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    const upcomingBirthdays = personnel
      .filter(p => {
        if (!p.date_of_birth) return false;
        const dob = new Date(p.date_of_birth);
        const birthdayThisYear = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
        if (birthdayThisYear < today) {
          birthdayThisYear.setFullYear(today.getFullYear() + 1);
        }
        return birthdayThisYear >= today && birthdayThisYear <= thirtyDaysFromNow;
      })
      .sort((a, b) => {
        const dobA = new Date(a.date_of_birth);
        const dobB = new Date(b.date_of_birth);
        const birthdayA = new Date(today.getFullYear(), dobA.getMonth(), dobA.getDate());
        const birthdayB = new Date(today.getFullYear(), dobB.getMonth(), dobB.getDate());
        return birthdayA - birthdayB;
      })
      .slice(0, 5);

    // Recent hires (last 90 days)
    const ninetyDaysAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
    const recentHires = personnel
      .filter(p => p.hire_date && new Date(p.hire_date) >= ninetyDaysAgo)
      .sort((a, b) => new Date(b.hire_date) - new Date(a.hire_date))
      .slice(0, 5);

    // Headcount trend (last 12 months)
    const headcountTrend = generateHeadcountTrend(personnel);

    // Recent activity feed
    const activities = generateActivityFeed(personnel, leaveRequests);

    return {
      totalEmployees,
      activeEmployees,
      onLeaveCount,
      openPositions: 3, // Hardcoded reasonable number
      turnoverRate,
      departmentDistribution,
      jobCategories,
      upcomingBirthdays,
      recentHires,
      headcountTrend,
      activities,
    };
  }, [personnel, leaveRequests]);
};

/**
 * Generate 12-month headcount trend
 */
const generateHeadcountTrend = (personnel) => {
  const today = new Date();
  const months = [];
  const headcounts = [];

  for (let i = 11; i >= 0; i--) {
    const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
    months.push(monthDate.toLocaleDateString('en-US', { month: 'short' }));

    const count = personnel.filter(p => {
      const hireDate = p.hire_date ? new Date(p.hire_date) : null;
      const termDate = p.termination_date ? new Date(p.termination_date) : null;
      return (
        (!hireDate || hireDate <= monthDate) &&
        (!termDate || termDate > monthDate)
      );
    }).length;
    headcounts.push(count);
  }

  return { months, headcounts };
};

/**
 * Generate activity feed from personnel data
 */
const generateActivityFeed = (personnel, leaveRequests) => {
  const activities = [];
  const today = new Date();

  // New hires
  const ninetyDaysAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
  personnel.forEach(p => {
    if (p.hire_date && new Date(p.hire_date) >= ninetyDaysAgo) {
      activities.push({
        id: `hire-${p.id}`,
        icon: UserPlus,
        description: `${p.first_name} ${p.last_name} hired as ${p.job_title}`,
        timestamp: new Date(p.hire_date),
        category: 'hire',
        color: colors.success,
      });
    }
  });

  // Status changes
  personnel.forEach(p => {
    if (p.status === 'terminated' && p.termination_date) {
      const termDate = new Date(p.termination_date);
      if (termDate >= ninetyDaysAgo) {
        activities.push({
          id: `term-${p.id}`,
          icon: LogOut,
          description: `${p.first_name} ${p.last_name} terminated`,
          timestamp: termDate,
          category: 'termination',
          color: colors.danger,
        });
      }
    }
  });

  // Leave requests approved
  leaveRequests.forEach(lr => {
    if (lr.status === 'approved') {
      activities.push({
        id: `leave-${lr.id}`,
        icon: Calendar,
        description: `Leave request approved: ${lr.employee_name || 'Employee'}`,
        timestamp: new Date(lr.created_date || new Date()),
        category: 'leave',
        color: colors.warning,
      });
    }
  });

  // Sort by timestamp descending and limit to 10
  return activities
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 10);
};

/**
 * KPI Card Component
 */
const KPICard = ({ icon: Icon, title, value, color, subtext }) => {
  return (
    <div
      style={{
        flex: 1,
        minWidth: '180px',
        padding: '20px',
        borderRadius: '12px',
        background: colors.background,
        boxShadow: neomorphicShadow,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      <Icon size={28} color={color} strokeWidth={1.5} />
      <div style={{ fontSize: '12px', color: colors.textSecondary, textAlign: 'center' }}>
        {title}
      </div>
      <div style={{ fontSize: '32px', fontWeight: '700', color: colors.text }}>
        {value}
      </div>
      {subtext && (
        <div style={{ fontSize: '11px', color: colors.textSecondary }}>
          {subtext}
        </div>
      )}
    </div>
  );
};

/**
 * Department Distribution Chart (horizontal bar chart)
 */
const DepartmentChart = ({ departments }) => {
  const maxCount = Math.max(...departments.map(d => d.count), 1);
  const chartHeight = departments.length * 45 + 40;

  return (
    <div
      style={{
        padding: '20px',
        borderRadius: '12px',
        background: colors.background,
        boxShadow: neomorphicShadow,
        flex: 1,
        minWidth: '300px',
      }}
    >
      <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: colors.text }}>
        Department Distribution
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxHeight: '400px', overflowY: 'auto' }}>
        {departments.map((dept, idx) => (
          <div key={dept.dept}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                {dept.dept}
              </span>
              <span style={{ fontSize: '13px', color: colors.textSecondary, fontWeight: '500' }}>
                {dept.count}
              </span>
            </div>
            <div
              style={{
                height: '20px',
                background: colors.background,
                borderRadius: '10px',
                overflow: 'hidden',
                boxShadow: neomorphicInsetShadow,
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(dept.count / maxCount) * 100}%`,
                  background: `linear-gradient(90deg, ${departmentColors[idx % departmentColors.length]}, ${departmentColors[(idx + 1) % departmentColors.length]})`,
                  borderRadius: '10px',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Headcount Trend Chart (SVG line chart)
 */
const HeadcountChart = ({ trend }) => {
  const { months, headcounts } = trend;
  const maxHeadcount = Math.max(...headcounts, 10);
  const chartWidth = 300;
  const chartHeight = 150;
  const padding = 30;
  const graphWidth = chartWidth - padding * 2;
  const graphHeight = chartHeight - padding * 2;

  // Scale headcounts to graph coordinates
  const points = headcounts.map((hc, idx) => ({
    x: padding + (idx / (headcounts.length - 1)) * graphWidth,
    y: padding + graphHeight - (hc / maxHeadcount) * graphHeight,
  }));

  const polylinePoints = points.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <div
      style={{
        padding: '20px',
        borderRadius: '12px',
        background: colors.background,
        boxShadow: neomorphicShadow,
        flex: 1,
        minWidth: '300px',
      }}
    >
      <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: colors.text }}>
        Headcount Trend (Last 12 Months)
      </h3>
      <svg
        width="100%"
        height="200"
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        style={{ background: 'transparent' }}
      >
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => (
          <line
            key={`gridh-${idx}`}
            x1={padding}
            y1={padding + graphHeight * (1 - ratio)}
            x2={chartWidth - padding}
            y2={padding + graphHeight * (1 - ratio)}
            stroke={colors.textSecondary}
            strokeWidth="0.5"
            opacity="0.3"
            strokeDasharray="2,2"
          />
        ))}

        {/* Gradient fill under line */}
        <defs>
          <linearGradient id="headcountGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.primary} stopOpacity="0.3" />
            <stop offset="100%" stopColor={colors.primary} stopOpacity="0" />
          </linearGradient>
        </defs>

        <polyline
          points={polylinePoints}
          fill="url(#headcountGrad)"
          stroke={colors.primary}
          strokeWidth="2"
        />

        {/* Data points */}
        {points.map((p, idx) => (
          <circle
            key={`point-${idx}`}
            cx={p.x}
            cy={p.y}
            r="3"
            fill={colors.primary}
            stroke="white"
            strokeWidth="1"
          />
        ))}

        {/* X-axis labels (every other month) */}
        {months.map((month, idx) => {
          if (idx % 2 === 0) {
            const x = padding + (idx / (months.length - 1)) * graphWidth;
            return (
              <text
                key={`month-${idx}`}
                x={x}
                y={chartHeight - 5}
                textAnchor="middle"
                fontSize="11"
                fill={colors.textSecondary}
              >
                {month}
              </text>
            );
          }
          return null;
        })}

        {/* Y-axis label */}
        <text
          x="10"
          y={padding + graphHeight / 2}
          textAnchor="middle"
          fontSize="11"
          fill={colors.textSecondary}
          transform={`rotate(-90 10 ${padding + graphHeight / 2})`}
        >
          Headcount
        </text>
      </svg>
    </div>
  );
};

/**
 * Quick Stats Section
 */
const QuickStats = ({ stats }) => {
  const statItems = [
    { label: 'Job Categories', items: stats.jobCategories, maxItems: 5 },
    { label: 'Upcoming Birthdays', items: stats.upcomingBirthdays, maxItems: 5 },
    { label: 'Recent Hires', items: stats.recentHires, maxItems: 5 },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginTop: '20px' }}>
      {/* Job Category Breakdown */}
      <div
        style={{
          padding: '20px',
          borderRadius: '12px',
          background: colors.background,
          boxShadow: neomorphicShadow,
        }}
      >
        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: colors.text }}>
          Job Categories
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {stats.jobCategories.slice(0, 5).map((cat, idx) => (
            <div key={cat.category}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: departmentColors[idx % departmentColors.length],
                    }}
                  />
                  <span style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                    {cat.category}
                  </span>
                </div>
                <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                  {cat.count} ({cat.percentage}%)
                </span>
              </div>
              <div
                style={{
                  height: '6px',
                  background: colors.background,
                  borderRadius: '3px',
                  overflow: 'hidden',
                  boxShadow: neomorphicInsetShadow,
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${cat.percentage}%`,
                    background: departmentColors[idx % departmentColors.length],
                    borderRadius: '3px',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Birthdays */}
      <div
        style={{
          padding: '20px',
          borderRadius: '12px',
          background: colors.background,
          boxShadow: neomorphicShadow,
        }}
      >
        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: colors.text }}>
          Upcoming Birthdays (30 Days)
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto' }}>
          {stats.upcomingBirthdays.length > 0 ? (
            stats.upcomingBirthdays.map(emp => (
              <div
                key={emp.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px',
                  borderRadius: '8px',
                  background: colors.background,
                  border: `1px solid rgba(46,139,152,0.1)`,
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.lightPrimary})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}
                >
                  {emp.first_name?.charAt(0)}{emp.last_name?.charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                    {emp.first_name} {emp.last_name}
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textSecondary }}>
                    {new Date(emp.date_of_birth).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: '12px', color: colors.textSecondary, textAlign: 'center', padding: '20px' }}>
              No upcoming birthdays
            </div>
          )}
        </div>
      </div>

      {/* Recent Hires */}
      <div
        style={{
          padding: '20px',
          borderRadius: '12px',
          background: colors.background,
          boxShadow: neomorphicShadow,
        }}
      >
        <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: colors.text }}>
          Recent Hires (90 Days)
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto' }}>
          {stats.recentHires.length > 0 ? (
            stats.recentHires.map(emp => (
              <div
                key={emp.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px',
                  borderRadius: '8px',
                  background: colors.background,
                  border: `1px solid rgba(98,187,70,0.2)`,
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${colors.success}, #7ec967)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600',
                  }}
                >
                  {emp.first_name?.charAt(0)}{emp.last_name?.charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', color: colors.text, fontWeight: '500' }}>
                    {emp.first_name} {emp.last_name}
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textSecondary }}>
                    {emp.job_title || 'N/A'}
                  </div>
                  <div style={{ fontSize: '10px', color: colors.textSecondary }}>
                    Hired: {new Date(emp.hire_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: '12px', color: colors.textSecondary, textAlign: 'center', padding: '20px' }}>
              No recent hires
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Recent Activity Feed
 */
const ActivityFeed = ({ activities }) => {
  const formatTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      style={{
        marginTop: '20px',
        padding: '20px',
        borderRadius: '12px',
        background: colors.background,
        boxShadow: neomorphicShadow,
      }}
    >
      <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: colors.text }}>
        Recent Activity
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
        {activities.length > 0 ? (
          activities.map(activity => {
            const IconComponent = activity.icon;
            return (
              <div
                key={activity.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '12px',
                  borderRadius: '8px',
                  background: colors.background,
                  borderLeft: `3px solid ${activity.color}`,
                  transition: 'transform 0.2s ease',
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: `${activity.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconComponent size={16} color={activity.color} strokeWidth={2} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', color: colors.text, fontWeight: '500', wordBreak: 'break-word' }}>
                    {activity.description}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <span style={{ fontSize: '11px', color: colors.textSecondary }}>
                      {formatTime(activity.timestamp)}
                    </span>
                    <span
                      style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: `${activity.color}20`,
                        color: activity.color,
                        fontWeight: '600',
                        textTransform: 'capitalize',
                      }}
                    >
                      {activity.category}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ fontSize: '12px', color: colors.textSecondary, textAlign: 'center', padding: '20px' }}>
            No recent activity
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Quick Action Button
 */
const QuickActionButton = ({ icon: Icon, label, onClick }) => {
  const [isPressed, setIsPressed] = React.useState(false);

  return (
    <button
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onClick={onClick}
      style={{
        flex: 1,
        minWidth: '140px',
        padding: '12px 16px',
        borderRadius: '12px',
        border: 'none',
        background: colors.background,
        boxShadow: isPressed
          ? `inset 3px 3px 8px rgba(186,199,216,0.45), inset -3px -3px 8px rgba(255,255,255,0.8)`
          : neomorphicShadow,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: '13px',
        fontWeight: '600',
        color: colors.primary,
        transition: 'all 0.2s ease',
      }}
    >
      <Icon size={18} strokeWidth={2} />
      {label}
    </button>
  );
};

/**
 * Main Dashboard Component
 */
export default function DashboardTab({ personnel = [], leaveRequests = [], onNavigate = () => {} }) {
  const stats = usePersonnelStats(personnel, leaveRequests);

  return (
    <div
      style={{
        padding: '24px',
        background: colors.background,
        minHeight: '100vh',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: colors.text }}>
          Personnel Dashboard
        </h1>
        <p style={{ margin: 0, fontSize: '13px', color: colors.textSecondary }}>
          Overview of your organization's workforce
        </p>
      </div>

      {/* KPI Cards Row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '30px' }}>
        <KPICard
          icon={Users}
          title="Total Employees"
          value={stats.totalEmployees}
          color={colors.primary}
        />
        <KPICard
          icon={UserCheck}
          title="Active Employees"
          value={stats.activeEmployees}
          color={colors.success}
          subtext={`${((stats.activeEmployees / stats.totalEmployees) * 100).toFixed(0)}% active`}
        />
        <KPICard
          icon={Clock}
          title="On Leave"
          value={stats.onLeaveCount}
          color={colors.warning}
        />
        <KPICard
          icon={Briefcase}
          title="Open Positions"
          value={stats.openPositions}
          color={colors.primary}
        />
        <KPICard
          icon={TrendingDown}
          title="Turnover Rate"
          value={`${stats.turnoverRate}%`}
          color={colors.danger}
        />
      </div>

      {/* Charts Section */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '30px' }}>
        <DepartmentChart departments={stats.departmentDistribution} />
        <HeadcountChart trend={stats.headcountTrend} />
      </div>

      {/* Quick Stats Grid */}
      <QuickStats stats={stats} />

      {/* Recent Activity Feed */}
      <ActivityFeed activities={stats.activities} />

      {/* Quick Action Buttons */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '30px' }}>
        <QuickActionButton
          icon={Plus}
          label="Add Employee"
          onClick={() => onNavigate('add-employee')}
        />
        <QuickActionButton
          icon={Users}
          label="View Directory"
          onClick={() => onNavigate('directory')}
        />
        <QuickActionButton
          icon={Calendar}
          label="Leave Requests"
          onClick={() => onNavigate('leave')}
        />
        <QuickActionButton
          icon={GitBranch}
          label="Org Chart"
          onClick={() => onNavigate('orgchart')}
        />
        <QuickActionButton
          icon={BarChart3}
          label="Run Report"
          onClick={() => onNavigate('reports')}
        />
      </div>
    </div>
  );
}
